import React, { useState } from 'react';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import useAppContext from 'flow/hooks/useAppContext';
import { extractName } from 'flow/utils';
import ContextMenu from 'flow/components/ContextMenu';
import ChatMemberContextMenu from './MessageMemberContextMenu';
import { twMerge } from 'tailwind-merge';
import { ChatMember } from 'flow/types/Chats';
import { TypedMap } from 'flow/types/TypedMap';
import { getMoreOptionsLabel } from 'flow/utils/a11y-utils';

interface Props {
    member: TypedMap<ChatMember>;
    isAdmin: boolean;
    isOwnMember: boolean;
    /* All three are the page's to work out, so that a row holds no store
     * subscription of its own. Asking here meant reaching for the whole
     * session and filtering and sorting every member, once per row, to learn
     * how many there are - and taking useExtension's four subscriptions along
     * with it, to compare a single key. */
    lazyLoadAvatar: boolean;
}

const MessagesMember = ({
    member,
    isAdmin,
    isOwnMember,
    lazyLoadAvatar,
}: Props) => {
    const { i18next } = useAppContext();
    const [contextMenuIsActive, setContextMenuIsActive] = useState(false);

    return (
        <div
            className={twMerge(
                'group flex h-12 items-center rounded-sm px-2 py-2',
                contextMenuIsActive ? 'bg-gray-100' : 'hover:bg-gray-100',
            )}
        >
            <div className="flex w-full flex-1 items-center gap-x-3">
                <ContactAvatar
                    contact={member.get('contact')}
                    fallBackBackgroundColorClass={
                        'group-hover:bg-gray-300 bg-gray-200'
                    }
                    size="small"
                    lazyLoad={lazyLoadAvatar}
                />

                <div className="flex gap-1 truncate text-gray-800">
                    {extractName(
                        member.get('contact'),
                        i18next.t('status.unknown'),
                    )}
                    {isOwnMember && (
                        <div className="text-gray-500">
                            ({i18next.t('chat.you')})
                        </div>
                    )}
                </div>
            </div>

            <div
                className={twMerge(
                    'flex gap-x-2',
                    contextMenuIsActive
                        ? 'hidden'
                        : 'group-focus-visible-within:hidden group-hover:hidden',
                )}
            >
                {isAdmin && (
                    <span className="rounded-sm border border-gray-300 bg-gray-100 px-2 py-1 text-xs tracking-wide text-gray-400 uppercase">
                        {i18next.t('chat.admin')}
                    </span>
                )}
            </div>

            <div
                className={twMerge(
                    'gap-x-2',
                    contextMenuIsActive
                        ? 'flex'
                        : 'sr-only-until-hover-or-focus flex',
                )}
            >
                {!isOwnMember && (
                    <ContextMenu
                        triggerLabel={getMoreOptionsLabel(
                            i18next,
                            extractName(member.get('contact')),
                        )}
                        preventOverflow={true}
                        setActive={setContextMenuIsActive}
                        lockScroll={{
                            scroll: document.querySelector('#messages-members'),
                            wrapper:
                                document.querySelector('#messages-members'),
                        }}
                    >
                        <ChatMemberContextMenu member={member} />
                    </ContextMenu>
                )}
            </div>
        </div>
    );
};

export default MessagesMember;
